import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Save, Loader2, Undo2, Globe, MapPin, Users, Store, Wand2,
  Download, Upload, AlertCircle,
} from 'lucide-react';
import { useAppContext } from '../../../context/AppContext';
import { secondaryService } from '../../../services/secondaryService';
import { configService } from '../../../services/configService';
import MantleGrid, { MantleGridButton } from '../../../components/MantleGrid';
import PickerPopover from '../../../components/PickerPopover';
import { FilterPopover } from '../../../components/gridFilters';
import { cell, GRID } from '../../../styles/gridStyles';
import { exportCustomerZones, parseCustomerZones } from '../../../utils/customerZonesExcel';

// =============================================================================
// Distribution › Route to Market › Delivery Zones & Customers
//
// WHO YOU DELIVER TO AND WHERE — all of it, on one page. Secondary Lanes &
// Rates next door answers the other half: who carries it and at what price.
//
// The three tabs were two pages, and the split was in the wrong place. Mapping
// a customer to a zone means reading the zone list, and half the time means
// adding a zone; doing that across a navigation, with an unsaved grid behind
// you, is the kind of friction that gets worked around by typing a zone code
// that does not exist.
//
//   ZONES     the geographies themselves. A ZONE IS A GEOGRAPHY A CARRIER
//             QUOTES AS ONE THING — which is why the list is metro / surrounds
//             / rural rather than by province. No rate card prices a 45 km
//             Johannesburg round together with a 380 km Limpopo one.
//             Master data: it outlives budget versions, rate cards and
//             customers, so nothing here is version data.
//
//   MAPPING   customer → zone, and how big a drop is. TWO fields, and the
//             mapping is 1:1 — a ship-to that needs its own zone is its own
//             customer in the master. Thirty stores delivered to is thirty
//             customers, not one customer with a share.
//
//   STORES    the store delivery calendar, by cluster and stream, defaulted by
//             format. Store COUNT is derived from md_sites.cluster_code.
//             Only present when store replenishment is switched on.
//
// SAVE BUTTONS, not autosave, on all three. A zone change reprices every lane
// leaving it — that is not an edit that should commit because focus moved.
// Adds and deletes on the store schedule still commit immediately, because
// they need a server id to edit against.
//
// SERVER-FILTERED TOP 100 on the two big grids, same contract as the volume
// grids: filters go to Postgres, the page shows what came back and says so.
// =============================================================================

const MODULE = 'MOD_4_MOVE';
const PAGE_LIMIT = 100;
const LOAD_UOMS = ['PALLET', 'CAGE', 'TOTE', 'TONNE'];
const STREAMS = ['AMBIENT', 'CHILLED', 'FROZEN'];

const fmtN = (n, dp = 0) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
const streamLabel = (s) => (s ? `${s[0]}${s.slice(1).toLowerCase()}` : '');
const H_SCROLLBAR = 14;

const field = 'text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500/40 disabled:opacity-30';
const numField = `${field} text-right`;
const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors';
const seg = (on) => `inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${on
  ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200'
  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'}`;

// A new zone row is a draft until saved: it has no server identity, so it
// carries a client key and is only ever matched by zone_code once it lands.
let draftSeq = 0;

const DeliveryZones = () => {
  const { selectedVersion, selectedSite } = useAppContext();

  const [tab, setTab] = useState('zones');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  // One popover for both grids — the discriminator says which one asked, so a
  // filter applied on Zones can never land on the mapping filter set.
  const [filterUI, setFilterUI] = useState(null);   // { grid, colId, options, x, y }

  // ---- Shared setup (zone list for pickers, store schedules, picks, config) --
  const [setupLoading, setSetupLoading] = useState(true);
  const [setup, setSetup] = useState({
    zones: [], storeSchedules: [],
    picks: { customers: [], storeFormats: [] },
    config: { store_replenishment_enabled: false },
  });

  // ---- Zones tab -----------------------------------------------------------
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState({});          // zone_code -> patch
  const [drafts, setDrafts] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState({ total: null, hasMore: false });
  const [offset, setOffset] = useState(0);
  const [sites, setSites] = useState([]);
  const [scope, setScope] = useState({ site_code: null, all_sites: true });

  // ---- Mapping tab ---------------------------------------------------------
  const [mapRows, setMapRows] = useState([]);
  const [mapDirty, setMapDirty] = useState({});    // customer_code -> patch
  const [mapLoading, setMapLoading] = useState(true);
  const [mapFilters, setMapFilters] = useState({});
  const [mapPage, setMapPage] = useState({ total: null, hasMore: false });
  const [mapOffset, setMapOffset] = useState(0);
  const [zonePicker, setZonePicker] = useState(null);   // { customer, x, y }
  const [importErrors, setImportErrors] = useState([]);
  const fileRef = useRef(null);

  // ---- Store schedule tab --------------------------------------------------
  const [schedDirty, setSchedDirty] = useState({});     // schedule_id -> patch
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({});

  const storeOn = !!setup.config.store_replenishment_enabled;

  // =========================================================================
  // LOADING
  // =========================================================================
  const loadSetup = useCallback(async () => {
    setSetupLoading(true);
    try {
      const res = await secondaryService.getSetup();
      const d = res.data || {};
      setSetup((prev) => ({ ...prev, ...d }));
      setSchedDirty({});
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the delivery setup.');
    } finally {
      setSetupLoading(false);
    }
  }, []);

  const loadZones = useCallback(async () => {
    setZonesLoading(true);
    try {
      const zres = await secondaryService.getZones({ filters, limit: PAGE_LIMIT, offset });
      setRows(zres.data || []);
      setPage({ total: zres.total ?? null, hasMore: !!zres.hasMore });
      setSites(zres.sites || []);
      setScope(zres.scope || { site_code: null, all_sites: true });
      setDirty({});
      setDrafts([]);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load delivery zones.');
    } finally {
      setZonesLoading(false);
    }
  }, [filters, offset]);

  const loadMapping = useCallback(async () => {
    setMapLoading(true);
    try {
      const res = await secondaryService.getAssignments({ filters: mapFilters, limit: PAGE_LIMIT, offset: mapOffset });
      setMapRows(res.data || []);
      setMapPage({ total: res.total ?? null, hasMore: !!res.hasMore });
      setMapDirty({});
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the customer mapping.');
    } finally {
      setMapLoading(false);
    }
  }, [mapFilters, mapOffset]);

  // Zones are NOT gated on selectedVersion. A geography does not belong to a
  // budget version, and gating the load meant a refresh before a version was
  // chosen showed an empty page with no explanation.
  useEffect(() => { loadZones(); }, [loadZones, selectedSite]);
  useEffect(() => { loadSetup(); }, [loadSetup, selectedSite, selectedVersion]);
  useEffect(() => { loadMapping(); }, [loadMapping, selectedSite, selectedVersion]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const run = async (fn, msg, reload) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      if (reload === 'setup') await loadSetup();
      else if (reload === 'zones') await loadZones();
      else if (reload === 'mapping') await loadMapping();
      if (msg) setToast(msg);
      return true;
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'That did not save.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const toggleStore = () => run(
    () => configService.setModuleConfig(MODULE, 'store_replenishment_enabled', storeOn ? 'N' : 'Y'),
    storeOn ? 'Store replenishment switched off.' : 'Store replenishment switched on.', 'setup'
  );

  // The full zone list, for the pickers and for validation — NOT the paged
  // grid, which holds at most 100 rows and would reject valid zone codes.
  const zoneByCode = useMemo(() => new Map(setup.zones.map((z) => [z.zone_code, z])), [setup.zones]);
  const customerZones = useMemo(() => setup.zones.filter((z) => z.zone_kind === 'CUSTOMER'), [setup.zones]);
  const storeZones = useMemo(() => setup.zones.filter((z) => z.zone_kind === 'STORE'), [setup.zones]);

  // A site code on its own is unreadable — "JHB01" means nothing to anyone who
  // did not build the site master. Every "served from" on this page and on
  // Secondary Lanes & Rates shows the code AND the description.
  const siteNameOf = useCallback(
    (code) => sites.find((s) => s.site_code === code)?.site_name || '',
    [sites]
  );
  const siteLabel = useCallback((code) => {
    if (!code) return '';
    const name = siteNameOf(code);
    return name ? `${code} · ${name}` : code;
  }, [siteNameOf]);

  const openFilter = (grid, colId, options, bounds) => setFilterUI({
    grid, colId, options,
    x: bounds ? bounds.x : 200,
    y: bounds ? bounds.y + bounds.height : 200,
  });

  // =========================================================================
  // ZONES TAB
  // =========================================================================
  const working = useMemo(
    () => [...rows.map((r) => ({ ...r, ...(dirty[r.zone_code] || {}) })), ...drafts],
    [rows, dirty, drafts]
  );
  const hasChanges = Object.keys(dirty).length > 0 || drafts.length > 0;

  const patch = (row, p) => {
    if (row.__draft) {
      setDrafts((prev) => prev.map((d) => (d.__draft === row.__draft ? { ...d, ...p } : d)));
      return;
    }
    setDirty((prev) => ({ ...prev, [row.zone_code]: { ...(prev[row.zone_code] || {}), ...p } }));
  };

  const addDraft = () => {
    draftSeq += 1;
    setDrafts((prev) => [...prev, {
      __draft: `d${draftSeq}`,
      zone_code: '', zone_name: '', zone_kind: 'CUSTOMER',
      // Default to the acting site, not the first in the list: a new zone
      // created while scoped to a site otherwise saves against a different
      // depot and immediately vanishes from the grid you made it in.
      origin_site_code: scope.site_code || sites[0]?.site_code || '', round_trip_km: 0,
      load_uom: 'PALLET', is_active: true,
      assignment_count: 0, store_count: 0, has_lane: false,
    }]);
  };

  const discard = () => { setDirty({}); setDrafts([]); };

  const save = async () => {
    const changed = [
      ...Object.keys(dirty).map((code) => working.find((w) => w.zone_code === code)),
      ...drafts,
    ].filter(Boolean);
    if (!changed.length) return;

    // Caught here rather than at the API so the message can name the row the
    // user is looking at, not a constraint.
    const blank = changed.find((z) => !String(z.zone_code || '').trim() || !String(z.zone_name || '').trim());
    if (blank) { setError('Every zone needs a code and a name.'); return; }
    const noOrigin = changed.find((z) => !String(z.origin_site_code || '').trim());
    if (noOrigin) { setError(`Zone "${noOrigin.zone_code}" is served from a depot — pick an origin site.`); return; }
    const dupe = changed.map((z) => z.zone_code).find((c, i, a) => a.indexOf(c) !== i);
    if (dupe) { setError(`Zone code "${dupe}" appears twice.`); return; }

    // Reload the shared setup too: a zone added here has to appear in the
    // mapping picker on the next tab without a page refresh, which is the
    // whole reason these three sit together.
    await run(async () => {
      await secondaryService.saveZones(changed.map((z) => ({
        zone_code: String(z.zone_code).trim(),
        zone_name: String(z.zone_name).trim(),
        zone_kind: z.zone_kind,
        origin_site_code: z.origin_site_code,
        round_trip_km: Number(z.round_trip_km) || 0,
        load_uom: z.load_uom,
        is_active: z.is_active !== false,
        notes: z.notes || null,
      })));
      await loadSetup();
    }, `${changed.length} zone(s) saved.`, 'zones');
  };

  const removeZone = (row) => {
    if (row.__draft) { setDrafts((prev) => prev.filter((d) => d.__draft !== row.__draft)); return; }
    run(async () => {
      await secondaryService.deleteZone(row.zone_code);
      await loadSetup();
    }, 'Zone deleted.', 'zones');
  };

  const columns = useMemo(() => ([
    { columnId: 'zone_code', width: 130, resizable: true, hasMenu: true },
    { columnId: 'zone_name', width: 230, resizable: true, hasMenu: true },
    { columnId: 'zone_kind', width: 120, resizable: true, hasMenu: true },
    { columnId: 'origin_site_code', width: 240, resizable: true, hasMenu: true },
    { columnId: 'round_trip_km', width: 120, resizable: true },
    { columnId: 'load_uom', width: 100, resizable: true, hasMenu: true },
    { columnId: 'assignment_count', width: 100, resizable: true },
    { columnId: 'store_count', width: 90, resizable: true },
    { columnId: 'has_lane', width: 90, resizable: true },
    { columnId: 'remove', width: 60, resizable: false },
  ]), []);

  const gridRows = useMemo(() => {
    const header = {
      rowId: 'header', height: GRID.size.headerRow,
      cells: [
        cell.header('Zone Code'), cell.header('Zone Name'), cell.header('Type'),
        cell.header('Served From'), cell.header('Round-trip km', { align: 'right' }),
        cell.header('Load Unit'),
        cell.header('Customers', { align: 'right' }), cell.header('Stores', { align: 'right' }),
        cell.header('Lane?'), cell.header(''),
      ],
    };
    const data = working.map((z, idx) => {
      const isDraft = !!z.__draft;
      const edited = !isDraft && !!dirty[z.zone_code];
      return {
        rowId: z.__draft || z.zone_code, height: GRID.size.dataRow,
        cells: [
          // A draft's code is editable; a saved one is the primary key and is
          // not something you rename in place.
          isDraft ? cell.editText(z.zone_code, { idx })
            : cell.label(`${edited ? '• ' : ''}${z.zone_code}`, { idx }),
          cell.editText(z.zone_name, { idx }),
          cell.editText(z.zone_kind === 'STORE' ? 'Store cluster' : 'Customer', { idx }),
          cell.editText(siteLabel(z.origin_site_code), { idx }),
          cell.number(Number(z.round_trip_km) || 0, { editable: true, idx }),
          cell.editText(z.load_uom, { idx }),
          cell.number(Number(z.assignment_count) || 0, { editable: false, idx }),
          cell.number(Number(z.store_count) || 0, { editable: false, idx }),
          cell.text(z.has_lane ? 'yes' : '—', { idx }),
          cell.text('✕', { idx }),
        ],
      };
    });
    return [header, ...data];
  }, [working, dirty, siteLabel]);

  const gridHeight = useMemo(
    () => GRID.size.headerRow + Math.max(1, working.length) * GRID.size.dataRow + 44 + H_SCROLLBAR,
    [working.length]
  );

  const rowOf = (rowId) => working.find((w) => (w.__draft || w.zone_code) === String(rowId));

  const handleChanges = (changes) => {
    changes.forEach((ch) => {
      const row = rowOf(ch.rowId);
      if (!row) return;
      const col = String(ch.columnId);
      const text = ch.newCell?.text != null ? String(ch.newCell.text).trim() : '';
      const val = ch.newCell?.value;

      if (col === 'zone_code' && row.__draft) patch(row, { zone_code: text.toUpperCase() });
      else if (col === 'zone_name') patch(row, { zone_name: text });
      else if (col === 'zone_kind') {
        // Typed free-hand, so accept anything that starts with the word — an
        // exact-match dropdown in a canvas grid is more friction than it buys.
        const kind = /^s/i.test(text) ? 'STORE' : 'CUSTOMER';
        patch(row, { zone_kind: kind, load_uom: kind === 'STORE' ? 'CAGE' : row.load_uom });
      } else if (col === 'origin_site_code') {
        // The cell reads "JHB01 · Johannesburg DC", so a re-typed or pasted
        // value can arrive in either half or in both. Take the code side if
        // the separator is there, then match on code OR name — otherwise
        // editing a cell you did not mean to change rewrites the site as the
        // literal string you were only reading.
        const typed = text.includes('·') ? text.split('·')[0].trim() : text;
        const hit = sites.find((s) => s.site_code.toLowerCase() === typed.toLowerCase()
          || (s.site_name || '').toLowerCase() === typed.toLowerCase());
        patch(row, { origin_site_code: hit ? hit.site_code : typed.toUpperCase() });
      } else if (col === 'load_uom') {
        const up = text.toUpperCase();
        patch(row, { load_uom: LOAD_UOMS.includes(up) ? up : row.load_uom });
      } else if (col === 'round_trip_km') patch(row, { round_trip_km: Math.max(0, Number(val) || 0) });
    });
  };

  const handleFocus = (loc) => {
    if (String(loc.columnId) !== 'remove') return true;
    const row = rowOf(loc.rowId);
    if (row) removeZone(row);
    return false;
  };

  // Filter options come from the loaded page plus the site master — enough to
  // be useful without a second round trip for a checklist.
  const onHeaderMenu = useCallback((columnId, bounds) => {
    if (!['zone_code', 'zone_name', 'zone_kind', 'origin_site_code', 'load_uom'].includes(columnId)) return;
    const source = columnId === 'origin_site_code' ? sites.map((s) => s.site_code) : rows.map((r) => r[columnId]);
    const options = [...new Set(source.map((v) => String(v ?? '')))].filter(Boolean).sort();
    openFilter('zones', columnId, options, bounds);
  }, [rows, sites]);

  const filterCount = Object.keys(filters).length;

  // =========================================================================
  // MAPPING TAB
  // =========================================================================
  const mapWorking = useMemo(
    () => mapRows.map((r) => ({ ...r, ...(mapDirty[r.customer_code] || {}) })),
    [mapRows, mapDirty]
  );
  const mapHasChanges = Object.keys(mapDirty).length > 0;

  const patchMap = (code, p) => setMapDirty((prev) => ({ ...prev, [code]: { ...(prev[code] || {}), ...p } }));

  const mapColumns = useMemo(() => ([
    { columnId: 'customer_code', width: 130, resizable: true, hasMenu: true },
    { columnId: 'customer_name', width: 250, resizable: true, hasMenu: true },
    { columnId: 'channel_name', width: 160, resizable: true, hasMenu: true },
    { columnId: 'zone_code', width: 140, resizable: true, hasMenu: true },
    { columnId: 'zone_name', width: 200, resizable: true },
    { columnId: 'avg_drop_size', width: 120, resizable: true },
    { columnId: 'annual_volume', width: 130, resizable: true },
    { columnId: 'implied_drops', width: 120, resizable: true },
  ]), []);

  const mapGridRows = useMemo(() => {
    const header = {
      rowId: 'header', height: GRID.size.headerRow,
      cells: [
        cell.header('Customer Code'), cell.header('Customer'), cell.header('Channel'),
        cell.header('Zone Code'), cell.header('Zone'),
        cell.header('Avg Drop Size', { align: 'right' }),
        cell.header('Volume / yr', { align: 'right' }),
        cell.header('Drops / yr', { align: 'right' }),
      ],
    };
    const data = mapWorking.map((r, idx) => {
      const edited = !!mapDirty[r.customer_code];
      const zone = zoneByCode.get(r.zone_code);
      const vol = Number(r.annual_volume) || 0;
      const size = Number(r.avg_drop_size) || 0;
      return {
        rowId: r.customer_code, height: GRID.size.dataRow,
        cells: [
          cell.label(`${edited ? '• ' : ''}${r.customer_code}`, { idx }),
          cell.text(r.customer_name, { idx }),
          cell.text(r.channel_name || '', { idx }),
          // Click to open a type-to-filter picker rather than free text: a zone
          // code typed by hand is a zone code typed wrong, and the error only
          // surfaces at Save.
          cell.text(r.zone_code || '— pick a zone —', { idx, color: r.zone_code ? undefined : 'var(--grid-muted)' }),
          cell.text(zone?.zone_name || (r.zone_code ? '?' : '— not mapped —'), { idx }),
          cell.number(size, { editable: true, idx }),
          cell.number(Math.round(vol), { editable: false, idx }),
          cell.number(size > 0 ? Math.round(vol / size) : 0, { editable: false, idx }),
        ],
      };
    });
    return [header, ...data];
  }, [mapWorking, mapDirty, zoneByCode]);

  const mapGridHeight = useMemo(
    () => GRID.size.headerRow + Math.max(1, mapWorking.length) * GRID.size.dataRow + 44 + H_SCROLLBAR,
    [mapWorking.length]
  );

  const handleMapChanges = (changes) => {
    changes.forEach((ch) => {
      if (String(ch.columnId) !== 'avg_drop_size') return;
      patchMap(String(ch.rowId), { avg_drop_size: Math.max(0, Number(ch.newCell?.value) || 0) });
    });
  };

  // Zone is picked, never typed. The popover filters as you type across code
  // and name, so "gaut" narrows to the Gauteng zones and "jhb" to the ones
  // dispatched from Johannesburg.
  const handleMapClick = (loc) => {
    if (String(loc.columnId) !== 'zone_code') return true;
    const b = loc.bounds;
    setZonePicker({
      customer: String(loc.rowId),
      x: b ? b.x : 240,
      y: b ? b.y + b.height : 240,
    });
    return false;
  };

  const saveMapping = () => {
    const changed = Object.keys(mapDirty).map((c) => mapWorking.find((r) => r.customer_code === c)).filter(Boolean);
    if (!changed.length) return;
    const bad = changed.find((r) => r.zone_code && !zoneByCode.has(r.zone_code));
    if (bad) { setError(`"${bad.zone_code}" is not a zone code. Add it on the Zones tab.`); return; }
    const zero = changed.find((r) => r.zone_code && !(Number(r.avg_drop_size) > 0));
    if (zero) { setError(`${zero.customer_name}: average drop size must be greater than zero — drops are volume ÷ drop size.`); return; }

    run(() => secondaryService.saveAssignments(changed.map((r) => ({
      customer_code: r.customer_code,
      zone_code: r.zone_code || null,
      avg_drop_size: Number(r.avg_drop_size) || 0,
      is_active: r.is_active !== false,
    }))), 'Mapping saved.', 'mapping');
  };

  // Export re-queries rather than dumping the page. The grid holds at most 100
  // rows, and a 100-line file that looks complete would unmap everyone else on
  // re-import.
  const handleExport = async () => {
    setBusy(true); setError(''); setImportErrors([]);
    try {
      const res = await secondaryService.getAssignments({ filters: mapFilters, limit: 50000, scope: 'export' });
      if (res.hasMore) setError('More than 50,000 customers match — the export was capped. Filter and export in parts.');
      exportCustomerZones({
        rows: res.data || [],
        zones: setup.zones,
        versionLabel: selectedVersion,
      });
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not build the export.');
    } finally { setBusy(false); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setError(''); setImportErrors([]);
    try {
      const { rows: parsed, errors } = await parseCustomerZones(file, {
        zones: setup.zones,
        customers: setup.picks.customers,
      });
      if (errors.length) { setImportErrors(errors.slice(0, 25)); return; }
      if (!parsed.length) { setError('No data rows in that file.'); return; }
      await secondaryService.saveAssignments(parsed);
      await loadMapping();
      setToast(`${parsed.length} row(s) imported.`);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not read that file.');
    } finally { setBusy(false); }
  };

  const onMapHeaderMenu = useCallback((columnId, bounds) => {
    if (!['customer_code', 'customer_name', 'channel_name', 'zone_code'].includes(columnId)) return;
    const source = columnId === 'zone_code'
      ? customerZones.map((z) => z.zone_code)
      : mapRows.map((r) => r[columnId]);
    const options = [...new Set(source.map((v) => String(v ?? '')))].filter(Boolean).sort();
    openFilter('mapping', columnId, options, bounds);
  }, [mapRows, customerZones]);

  // =========================================================================
  // STORE SCHEDULE TAB
  // =========================================================================
  const schedWorking = useMemo(
    () => setup.storeSchedules.map((s) => ({ ...s, ...(schedDirty[s.schedule_id] || {}) })),
    [setup.storeSchedules, schedDirty]
  );
  const schedHasChanges = Object.keys(schedDirty).length > 0;

  const schedColumns = useMemo(() => ([
    { columnId: 'cluster_code', width: 170, resizable: true },
    { columnId: 'stream', width: 120, resizable: true },
    { columnId: 'store_format', width: 160, resizable: true },
    { columnId: 'deliveries_pm', width: 140, resizable: true },
    { columnId: 'store_count', width: 110, resizable: true },
    { columnId: 'drops_pm', width: 130, resizable: true },
    { columnId: 'remove', width: 60, resizable: false },
  ]), []);

  const schedGridRows = useMemo(() => {
    const header = {
      rowId: 'header', height: GRID.size.headerRow,
      cells: [
        cell.header('Cluster'), cell.header('Stream'), cell.header('Store Format'),
        cell.header('Deliveries / mo', { align: 'right' }),
        cell.header('Stores', { align: 'right' }),
        cell.header('Drops / mo', { align: 'right' }),
        cell.header(''),
      ],
    };
    const data = schedWorking.map((s, idx) => {
      const edited = !!schedDirty[s.schedule_id];
      const freq = Number(s.deliveries_pm) || 0;
      const stores = Number(s.store_count) || 0;
      return {
        rowId: s.schedule_id, height: GRID.size.dataRow,
        cells: [
          cell.label(`${edited ? '• ' : ''}${s.cluster_code}`, { idx }),
          cell.text(streamLabel(s.stream), { idx }),
          cell.text(s.store_format || 'all formats', { idx }),
          cell.number(freq, { editable: true, idx }),
          cell.number(stores, { editable: false, idx }),
          cell.number(stores * freq, { editable: false, idx }),
          cell.text('✕', { idx }),
        ],
      };
    });
    return [header, ...data];
  }, [schedWorking, schedDirty]);

  const handleSchedChanges = (changes) => {
    changes.forEach((ch) => {
      if (String(ch.columnId) !== 'deliveries_pm') return;
      const id = String(ch.rowId);
      setSchedDirty((prev) => ({ ...prev, [id]: { deliveries_pm: Math.max(0, Number(ch.newCell?.value) || 0) } }));
    });
  };

  const saveSchedules = () => {
    const changed = Object.keys(schedDirty).map((id) => schedWorking.find((s) => s.schedule_id === id)).filter(Boolean);
    if (!changed.length) return;
    run(() => secondaryService.saveStoreSchedules(changed.map((s) => ({
      cluster_code: s.cluster_code, stream: s.stream,
      store_format: s.store_format || null,
      deliveries_pm: Number(s.deliveries_pm) || 0,
    }))), 'Schedule saved.', 'setup');
  };

  // =========================================================================
  // RENDER
  // =========================================================================
  const TABS = [
    { id: 'zones', label: 'Zones', icon: MapPin },
    { id: 'mapping', label: 'Customer Mapping', icon: Users },
    ...(storeOn ? [{ id: 'stores', label: 'Store Schedule', icon: Store }] : []),
  ];

  // Switching store replenishment off while looking at its tab would leave the
  // page on a tab that no longer exists.
  useEffect(() => { if (tab === 'stores' && !storeOn) setTab('zones'); }, [tab, storeOn]);

  const unmapped = useMemo(() => mapWorking.filter((r) => !r.zone_code).length, [mapWorking]);

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-3">
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Delivery Zones &amp; Customers</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          The geographies you deliver into, which zone each customer sits in, and how often own stores are replenished. Who carries each lane and at what rate lives on Secondary Lanes &amp; Rates.
        </p>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} className={seg(tab === t.id)}>
                  <Icon className="w-3.5 h-3.5" />{t.label}
                  {t.id === 'mapping' && unmapped > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
                      title={`${unmapped} customer(s) on this page have no zone`}>{unmapped}</span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
            <button onClick={toggleStore} disabled={busy}
              title="admin_config MOD_4_MOVE / store_replenishment_enabled — off for a manufacturer, and then store clusters and their calendar are simply not here"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border transition-colors disabled:opacity-40 ${storeOn
                ? 'bg-violet-100 text-violet-700 border-violet-300 dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-500/40'
                : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-600'}`}>
              <Store className="w-3.5 h-3.5" />Store replenishment {storeOn ? 'on' : 'off'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-3">
        {error && <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
        {toast && <div className="rounded-xl border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">{toast}</div>}
        {importErrors.length > 0 && (
          <div className="rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2">
            <p className="m-0 mb-1 flex items-center gap-1.5 text-xs font-bold text-amber-700 dark:text-amber-400">
              <AlertCircle className="w-3.5 h-3.5" /> Import rejected — {importErrors.length} problem(s). Nothing was written.
            </p>
            <ul className="m-0 pl-5 list-disc text-[11px] text-amber-700 dark:text-amber-300 space-y-0.5">
              {importErrors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}

        {/* ================= ZONES ================= */}
        {tab === 'zones' && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              {/* The list is scoped to the acting site: a zone belongs to the
                  depot that dispatches it. Said out loud, because a scoped list
                  and an unscoped one look identical and the only way to tell
                  them apart was to already know the answer. */}
              <p className="m-0 text-xs">
                {scope.all_sites ? (
                  <span className="inline-flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                    <Globe className="w-3.5 h-3.5" />
                    <strong>All Sites</strong> — every zone in the tenant. Pick a site in the top bar to see only the zones dispatched from it.
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-sky-50 dark:bg-sky-500/10 border border-sky-200 dark:border-sky-500/25 text-sky-700 dark:text-sky-300">
                    <MapPin className="w-3.5 h-3.5" />
                    Showing zones dispatched from <strong>{scope.site_code}</strong>
                    {sites.find((s) => s.site_code === scope.site_code)?.site_name
                      ? ` · ${sites.find((s) => s.site_code === scope.site_code).site_name}` : ''}
                    . Switch to All Sites in the top bar to see the rest.
                  </span>
                )}
              </p>
              <div className="flex items-center gap-2 shrink-0">
                <button className={btn} onClick={addDraft} disabled={busy}><Plus className="w-3.5 h-3.5" /> Add zone</button>
                <button className={btn} onClick={discard} disabled={!hasChanges || busy}><Undo2 className="w-3.5 h-3.5" /> Discard</button>
                <button className={btnPrimary} onClick={save} disabled={!hasChanges || busy}>
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Save{hasChanges ? ` (${Object.keys(dirty).length + drafts.length})` : ''}
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden" style={{ height: gridHeight }}>
              <MantleGrid
                rows={gridRows} columns={columns} isLoading={zonesLoading}
                onCellsChanged={handleChanges}
                onFocusLocationChanging={handleFocus}
                onHeaderMenuClick={onHeaderMenu}
                hint="Type in a cell to edit; nothing is written until you press Save. ▾ on a heading filters in the database, not the browser. Round-trip km is measured out and back from the origin depot — per-km rate lines multiply straight through it."
                toolbar={<>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    {page.total != null
                      ? `${fmtN(page.total)} matching · showing ${fmtN(rows.length)}`
                      : `first ${fmtN(rows.length)}${page.hasMore ? '+' : ''}`}
                    {filterCount > 0 && ` · ${filterCount} filter${filterCount === 1 ? '' : 's'}`}
                  </span>
                  {filterCount > 0 && <MantleGridButton onClick={() => { setFilters({}); setOffset(0); }}>Clear filters</MantleGridButton>}
                  <MantleGridButton onClick={() => setOffset(Math.max(0, offset - PAGE_LIMIT))} disabled={offset === 0}>Prev</MantleGridButton>
                  <MantleGridButton onClick={() => setOffset(offset + PAGE_LIMIT)} disabled={!page.hasMore}>Next</MantleGridButton>
                </>}
              />
            </div>

            <p className="m-0 text-[11px] text-slate-400">
              A zone is what a carrier quotes as one thing — so metro, surrounds and rural are separate zones, not one province.
              <strong> Customers</strong> and <strong>Stores</strong> are rollups of how many are mapped here; <strong>Lane?</strong> says whether the current budget version prices it.
              A zone with no lane costs nothing and harms nothing; it is a geography nobody has priced yet. Deleting a zone that still has stores attached is refused rather than cascaded.
            </p>
          </div>
        )}

        {/* ================= MAPPING ================= */}
        {tab === 'mapping' && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="m-0 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Customer → Zone</p>
              <div className="flex flex-wrap items-center gap-2">
                <button className={btn} disabled title="Not wired yet — see the note below">
                  <Wand2 className="w-3.5 h-3.5" /> Add drop size
                </button>
                <button className={btn} onClick={handleExport} disabled={busy}><Download className="w-3.5 h-3.5" /> Export</button>
                <button className={btn} onClick={() => fileRef.current?.click()} disabled={busy}><Upload className="w-3.5 h-3.5" /> Import</button>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImportFile} />
                <button className={btn} onClick={() => setMapDirty({})} disabled={!mapHasChanges || busy}><Undo2 className="w-3.5 h-3.5" /> Discard</button>
                <button className={btnPrimary} onClick={saveMapping} disabled={!mapHasChanges || busy}>
                  <Save className="w-3.5 h-3.5" /> Save{mapHasChanges ? ` (${Object.keys(mapDirty).length})` : ''}
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden" style={{ height: mapGridHeight }}>
              <MantleGrid
                rows={mapGridRows} columns={mapColumns} isLoading={mapLoading}
                onCellsChanged={handleMapChanges}
                onFocusLocationChanging={handleMapClick}
                onHeaderMenuClick={onMapHeaderMenu}
                hint="Click a Zone cell and type to search — it narrows on code and name. Avg Drop Size is typed directly. Nothing is written until you press Save. ▾ on a heading filters in the database."
                toolbar={<>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    {mapPage.total != null
                      ? `${fmtN(mapPage.total)} matching · showing ${fmtN(mapRows.length)}`
                      : `first ${fmtN(mapRows.length)}${mapPage.hasMore ? '+' : ''}`}
                    {Object.keys(mapFilters).length > 0 && ` · ${Object.keys(mapFilters).length} filter(s)`}
                  </span>
                  {Object.keys(mapFilters).length > 0 && (
                    <MantleGridButton onClick={() => { setMapFilters({}); setMapOffset(0); }}>Clear filters</MantleGridButton>
                  )}
                  <MantleGridButton onClick={() => setMapOffset(Math.max(0, mapOffset - PAGE_LIMIT))} disabled={mapOffset === 0}>Prev</MantleGridButton>
                  <MantleGridButton onClick={() => setMapOffset(mapOffset + PAGE_LIMIT)} disabled={!mapPage.hasMore}>Next</MantleGridButton>
                </>}
              />
            </div>

            <div className="rounded-lg border border-sky-200 dark:border-sky-500/25 bg-sky-50/60 dark:bg-sky-500/5 p-2.5">
              <p className="m-0 text-[11px] text-slate-600 dark:text-slate-300">
                <strong>Add drop size</strong> is not wired yet. When it is, it will read the customer&rsquo;s ORDER HISTORY, work out the average
                order size per delivery, and write it into the Avg Drop Size column for every row that has none — leaving anything already
                entered alone. Use the <strong>median</strong> rather than the mean: a handful of full-truck orders drags the average up, which
                understates drops and quietly understates the budget. Two orders shipped to one place on one day are ONE drop, so count
                distinct (customer × delivery date), not order lines.
              </p>
            </div>

            <p className="m-0 text-[11px] text-slate-400">
              One row per customer — the mapping is <strong>1:1</strong>. Thirty stores you deliver to are thirty customers in the master, not one customer with a share.
              <strong> Drops = volume ÷ average drop size</strong>, and that is the only number this page needs against a customer.
              Every active customer is listed whether mapped or not, because an unmapped one is the thing you most need to see.
              A zone that is missing gets added on the <strong>Zones</strong> tab and shows up in the picker straight away.
            </p>
          </div>
        )}

        {/* ================= STORE SCHEDULE ================= */}
        {tab === 'stores' && storeOn && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="m-0 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Store Delivery Schedule</p>
              <div className="flex items-center gap-2">
                <button className={btn} disabled={busy} onClick={() => { setAdding(true); setDraft({ stream: 'AMBIENT', deliveries_pm: 22 }); }}>
                  <Plus className="w-3.5 h-3.5" /> Add schedule
                </button>
                <button className={btn} onClick={() => setSchedDirty({})} disabled={!schedHasChanges || busy}><Undo2 className="w-3.5 h-3.5" /> Discard</button>
                <button className={btnPrimary} onClick={saveSchedules} disabled={!schedHasChanges || busy}>
                  <Save className="w-3.5 h-3.5" /> Save{schedHasChanges ? ` (${Object.keys(schedDirty).length})` : ''}
                </button>
              </div>
            </div>

            {adding && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 flex flex-wrap items-end gap-2">
                <select className={field} value={draft.cluster_code || ''} onChange={(e) => setDraft({ ...draft, cluster_code: e.target.value })}>
                  <option value="">Cluster…</option>
                  {storeZones.map((z) => <option key={z.zone_code} value={z.zone_code}>{z.zone_name}</option>)}
                </select>
                <select className={field} value={draft.stream} onChange={(e) => setDraft({ ...draft, stream: e.target.value })}>
                  {STREAMS.map((s) => <option key={s} value={s}>{streamLabel(s)}</option>)}
                </select>
                <select className={field} value={draft.store_format || ''} onChange={(e) => setDraft({ ...draft, store_format: e.target.value || null })}>
                  <option value="">All formats (default)</option>
                  {(setup.picks.storeFormats || []).map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <input type="number" min="0" className={numField} style={{ width: 110 }} placeholder="Deliveries/mo"
                  value={draft.deliveries_pm ?? ''} onChange={(e) => setDraft({ ...draft, deliveries_pm: e.target.value })} />
                <button className={btn} disabled={busy} onClick={async () => {
                  if (await run(() => secondaryService.saveStoreSchedule(draft), 'Schedule added.', 'setup')) setAdding(false);
                }}>Add</button>
                <button className={btn} onClick={() => setAdding(false)}>Cancel</button>
              </div>
            )}

            {storeZones.length === 0 && (
              <p className="m-0 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-3 text-xs text-amber-700 dark:text-amber-300">
                No store clusters yet. A cluster is a zone of type <strong>Store cluster</strong> — add one on the Zones tab first.
              </p>
            )}

            <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
              style={{ height: GRID.size.headerRow + Math.max(1, schedWorking.length) * GRID.size.dataRow + 44 + H_SCROLLBAR }}>
              <MantleGrid
                rows={schedGridRows} columns={schedColumns} isLoading={setupLoading}
                onCellsChanged={handleSchedChanges}
                onFocusLocationChanging={(loc) => {
                  if (String(loc.columnId) !== 'remove') return true;
                  const s = schedWorking.find((x) => x.schedule_id === String(loc.rowId));
                  if (s) run(() => secondaryService.deleteStoreSchedule(s.schedule_id), 'Schedule removed.', 'setup');
                  return false;
                }}
                hint="Deliveries per month is the only editable column — everything else is a rollup. Nothing is written until you press Save."
              />
            </div>

            <p className="m-0 text-[11px] text-slate-400">
              <strong>Stores</strong> is a rollup of <code>md_sites.cluster_code</code>, never an input — 2 000 stores × 3 streams × 12 periods is exactly the data entry this design avoids.
              A row with a <strong>format</strong> overrides the all-formats default for those stores only, which is what lets a whole estate share four rows.
            </p>
          </div>
        )}
      </div>

      {zonePicker && (
        <PickerPopover
          anchor={{ x: zonePicker.x, y: zonePicker.y }}
          value={mapWorking.find((r) => r.customer_code === zonePicker.customer)?.zone_code}
          options={customerZones.map((z) => ({
            code: z.zone_code,
            description: z.zone_name,
            // Which depot dispatches it, by name — the same zone name can be
            // served from two depots and only the description tells them apart.
            hint: z.origin_site_name ? `${z.origin_site_code} · ${z.origin_site_name}` : (z.origin_site_code || ''),
          }))}
          placeholder="Search zone…"
          emptyText="No zone matches. Add it on the Zones tab."
          onPick={(code) => { patchMap(zonePicker.customer, { zone_code: code }); setZonePicker(null); }}
          onClear={() => { patchMap(zonePicker.customer, { zone_code: null }); setZonePicker(null); }}
          clearLabel="Not delivered — unmap"
          onClose={() => setZonePicker(null)}
        />
      )}

      {filterUI && (
        <FilterPopover
          anchor={{ x: filterUI.x, y: filterUI.y }}
          options={filterUI.options}
          filter={(filterUI.grid === 'zones' ? filters : mapFilters)[filterUI.colId]}
          onApply={(f) => {
            const setter = filterUI.grid === 'zones' ? setFilters : setMapFilters;
            setter((prev) => {
              const next = { ...prev };
              if (!f || (!f.text && !(f.values && f.values.length))) delete next[filterUI.colId];
              else next[filterUI.colId] = f;
              return next;
            });
            if (filterUI.grid === 'zones') setOffset(0); else setMapOffset(0);
            setFilterUI(null);
          }}
          onClose={() => setFilterUI(null)}
        />
      )}
    </div>
  );
};

export default DeliveryZones;
